import axios from 'axios';
import { isReady } from 'app/services/jwtService/isReady';
import { getLoggedInUsers } from 'app/services/jwtService/getLoggedInUsers';
import { getXSRFToken } from 'utils/getXSRFToken';
import { setXSRFToken } from 'utils/setXSRFToken';
import NotificationService from 'app/services/NotificationService';
import 'utils/authSync';

function newAbortSignal(timeoutMs) {
	const ctrl = new AbortController();
	const t = setTimeout(() => ctrl.abort(), timeoutMs || 0);
	ctrl.signal.addEventListener('abort', () => clearTimeout(t));
	return ctrl.signal;
}

export const api = axios.create({
	baseURL: localStorage.getItem('baseUrl') || process.env.REACT_APP_API_URL,
	withCredentials: true
});

let isRequestInProgress = false;
export const requestQueue = [];

export const processNextRequest = () => {
	if (requestQueue.length > 0) {
		const next = requestQueue.shift();
		next && next();
	} else {
		isRequestInProgress = false;
	}
};

api.interceptors.request.use(
	async config => {
		await isReady.wait();

		if (!sessionStorage.getItem('activeAccount')) {
			const cached = localStorage.getItem('lastActiveAccount');
			if (cached) sessionStorage.setItem('activeAccount', cached);
		}

		return new Promise(resolve => {
			const executeRequest = () => {
				isRequestInProgress = false; // turning off request queue
				// isRequestInProgress = true;

				config.headers['XSRF-TOKEN'] = getXSRFToken();

				if (config.method === 'get') {
					config.signal = newAbortSignal(10000);
				}

				const auth = String(config.headers.Authorization || '');
				if (/^basic\s*$/i.test(auth)) {
					config.headers.Authorization = 'Basic ZnN0ZmR1c2VyOmZzdGZkcHN3ZA==';
				} else {
					const userId = JSON.parse(sessionStorage.getItem('activeAccount') || 'null')?.id;
					if (userId) config.headers.UserId = userId;
				}

				resolve(config);
			};

			isRequestInProgress ? requestQueue.push(executeRequest) : executeRequest();
		});
	},
	error => Promise.reject(error)
);

api.interceptors.response.use(
	response => {
		const xsrfToken = response.headers['xsrf-token'];
		if (xsrfToken) setXSRFToken(xsrfToken);

		isRequestInProgress = false;
		processNextRequest();
		return response;
	},
	async error => {
		console.log('error', { ...error });
		const xsrfToken = error?.response?.headers['xsrf-token'];
		if (xsrfToken) setXSRFToken(xsrfToken);

		isRequestInProgress = false;
		processNextRequest();

		const originalRequest = error.config;

		if (error?.response?.status === 406) throw error;

		//  && !originalRequest.__isRetry
		if (error?.response?.status === 418) {
			console.log('hello');
			await getLoggedInUsers();

			const accounts = JSON.parse(localStorage.getItem('accounts') || '{}');
			const activeAccount = JSON.parse(sessionStorage.getItem('activeAccount') || '{}');

			delete accounts[activeAccount?.phone];
			const phones = Object.keys(accounts);

			if (phones.length) {
				const newActive = accounts[phones[0]];
				sessionStorage.setItem('activeAccount', JSON.stringify(newActive));
				localStorage.setItem('lastActiveAccount', JSON.stringify(newActive)); //  ← sync uchun
			} else {
				localStorage.removeItem('accounts');
				localStorage.removeItem('xsrf-tokens');
				localStorage.removeItem('lastActiveAccount');
				sessionStorage.clear();
			}

			originalRequest.__isRetry = true;

			NotificationService.notify(error?.response?.data?.message, 'error');

			// return api(originalRequest);
			setTimeout(() => {
				window.location.reload();
			}, 1000);
		}

		if (error?.response) {
			const requestHeaders = error.config?.headers;

			// if (!error.config.url.includes('logger')) {
			// 	sendApiErrorToBackend(error, requestHeaders);
			// }

			return handleError(error?.response, xsrfToken);
		}

		throw error;
	}
);

// Error handling function
// function handleError(response, token) {
// 	const { status, data } = response;

// 	if (status && status >= 400 && status <= 600) {
// 		const messages = data?.message || data?.error;

// 		if (status === 429) throw new Error('Слишком много запросов, повторите попытку через минуту.');

// 		if (typeof messages === 'string' || Array.isArray(messages)) {
// 			if (Array.isArray(messages)) {
// 				const [message] = messages;

// 				throw new Error(message);
// 			}

// 			throw new Error(messages);
// 		}

// 		throw Error(String(response));
// 	}

// 	if ('data' in response) return response;
// }
async function handleError(response, token) {
	const { status, data } = response;

	if (status && status >= 400 && status <= 600) {
		let messages;

		if (status === 429) throw new Error('Слишком много запросов, повторите попытку через минуту.');

		if (data instanceof Blob) {
			try {
				const text = await data.text();
				const json = JSON.parse(text);
				messages = json?.message || json?.error || text;
			} catch (e) {
				messages = 'Unknown error';
			}
		} else {
			messages = data?.message || data?.error;
		}

		if (typeof messages === 'string') {
			throw new Error(messages);
		}

		if (Array.isArray(messages)) {
			throw new Error(messages[0]);
		}

		throw new Error('Unknown error');
	}

	return response;
}

async function sendApiErrorToBackend(error, requestHeaders) {
	const { status, data, config } = error.response;

	const errorPayload = {
		message: JSON.stringify(data?.message) || 'Unknown API Error',
		name: 'API Error',
		stack: error.stack,
		path: config.url,
		extra: JSON.stringify({
			method: config.method,
			timestamp: new Date().toISOString(),
			status,
			requestHeaders
		})
	};

	try {
		await api({
			method: 'POST',
			url: 'logger',
			data: JSON.stringify(errorPayload),
			headers: { 'Content-Type': 'application/json', 'XSRF-TOKEN': undefined }
		});
	} catch (err) {
		console.error('Failed to send API error to backend:', err);
	}
}
